import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { cn } from "@design-lab/shared/lib/tailwindcss";
import { useEffect, useRef, type ReactNode } from "react";

type BaseMenuItemProps = {
  icon: ReactNode;
  label: string;
  isActive?: boolean;
  onClick?: () => void;
  isAIDesign?: boolean;
};

function MobileMenuItem({
  icon,
  label,
  isActive = false,
  onClick,
  isAIDesign = false
}: BaseMenuItemProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isActive && buttonRef.current) {
      buttonRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center"
      });
    }
  }, [isActive]);

  const getButtonClasses = () => {
    const base =
      "box-border flex h-10 items-center gap-2 rounded-t-lg border-b-2 px-4 py-3 transition-colors";

    const variants = {
      inactive: "border-b-gray-200 text-gray-900",
      aiDesign: "bg-cta-gradient border-b-transparent font-semibold text-white",
      active: "border-b-blue-700 font-semibold text-gray-900"
    };

    let variant = variants.inactive;
    if (isActive) {
      variant = isAIDesign ? variants.aiDesign : variants.active;
    }

    return cn(base, variant);
  };

  const getIconClasses = () => {
    const base = "flex size-6 items-center justify-center";

    const variants = {
      inactive: "text-gray-500",
      aiDesign: "text-white",
      active: "text-blue-700"
    };

    let variant = variants.inactive;
    if (isActive) {
      variant = isAIDesign ? variants.aiDesign : variants.active;
    }

    return cn(base, variant);
  };

  return (
    <button
      ref={buttonRef}
      onClick={onClick}
      className={getButtonClasses()}
      data-testid={`menu-item-${label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
    >
      <div className={getIconClasses()}>{icon}</div>
      <span className="whitespace-nowrap text-sm">{label}</span>
    </button>
  );
}

function DesktopMenuItem({
  icon,
  label,
  isActive = false,
  onClick,
  isAIDesign = false
}: BaseMenuItemProps) {
  const getButtonClasses = () => {
    const base =
      "flex h-10 w-44 cursor-pointer items-center space-x-3 rounded-xl pl-4 pr-3 text-left transition-colors";

    const variants = {
      inactive: "bg-transparent text-gray-700 hover:bg-gray-50",
      aiDesign: "bg-cta-gradient text-white",
      active: "bg-gray-900 text-white"
    };

    let variant = variants.inactive;
    if (isActive) {
      variant = isAIDesign ? variants.aiDesign : variants.active;
    }

    return cn(base, variant);
  };

  return (
    <button
      onClick={onClick}
      className={getButtonClasses()}
      data-testid={`menu-item-${label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
    >
      <div className="flex size-6 items-center justify-center">{icon}</div>
      <span className="text-sm font-semibold leading-5">{label}</span>
    </button>
  );
}

export function MenuItem(props: BaseMenuItemProps) {
  const { isDesktop } = useResponsive();

  return isDesktop ? <DesktopMenuItem {...props} /> : <MobileMenuItem {...props} />;
}
